import type { RadioGroupItemProps } from '@radix-ui/react-radio-group';
import * as RadioGroup from '@radix-ui/react-radio-group';
import { ForwardedRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { coreTokens } from '../../../core';
import { theme as commonTheme } from '../common_input.theme';
import { useInputRadioContext } from './_input_radio.context';
import { theme } from './input_radio.theme';

const overlayShow = keyframes`
	from {
		opacity: 0;
		transform: scale(0);
	}
  to {
		opacity: 1;
		transform: scale(1);
	}
`;

const elements = {
	item: styled(RadioGroup.Item)`
		--background: none;
		--radio-border-color: ${theme.getValue('indicatorBorderColor')};
		--radio-background: ${theme.getValue('indicatorBackground')};
		--radio-outline: none;
		--indicator-background: ${theme.getValue('indicatorBorderColor')};

		all: unset;

		user-select: none;
		-webkit-tap-highlight-color: transparent;
		-webkit-user-drag: none;
		-webkit-touch-callout: none;

		box-sizing: border-box;
		display: flex;
		gap: ${theme.getValue('itemGap')};
		align-items: center;
		cursor: default;
		font: ${theme.getValue('itemFont')};
		color: ${theme.getValue('itemTextColor')};
		background: var(--background);
		border-radius: ${coreTokens.borderRadius.xSmall};
		transition:
			border 150ms ease-out,
			background 150ms ease-out,
			box-shadow 50ms ease-out;

		&[data-state='checked'] {
			font: ${theme.getValue('checkedItemFont')};

			--radio-border-color: ${theme.getValue('checkedIndicatorBorderColor')};
			--indicator-background: ${theme.getValue('checkedIndicatorBorderColor')};
		}

		&:focus-visible {
			outline: 2px solid ${theme.getValue('focusColor')};
			outline-offset: 2px;
		}

		&[data-variant='none'] {
			&:hover {
				--radio-border-color: ${theme.getValue('checkedIndicatorBorderColor')};
			}

			&:active {
				--radio-outline: 0 0 0 2px ${theme.getValue('activeIndicatorOutlineColor')};
			}

			&[data-invalid='true'] {
				--radio-border-color: ${commonTheme.getValue('invalidBorderColor')};
			}
		}

		&[data-variant='outlined'] {
			padding: ${theme.getValue('outlinedItemPadding')};
			border: 1px solid ${theme.getValue('outlinedItemBorderColor')};
			min-height: ${theme.getValue('outlinedItemMinHeight')};
			border-radius: ${theme.getValue('outlinedItemBorderRadius')};

			&:hover {
				border-color: ${theme.getValue('hoverOutlinedItemBorderColor')};
			}

			&:active {
				box-shadow: 0 0 0 2px ${theme.getValue('activeOutlinedItemOutlineColor')};
			}

			&[data-state='checked'] {
				--background: ${theme.getValue('checkedOutlinedItemBackground')};
			}

			&[data-invalid='true'] {
				border-color: ${commonTheme.getValue('invalidBorderColor')};
			}
		}

		// Disabled/ReadOnly
		&[data-read-only='true'],
		&[data-disabled='true'] {
			--radio-border-color: ${theme.getValue('disabledIndicatorBorderColor')};
			--radio-background: ${theme.getValue('disabledIndicatorBackground')};
			--indicator-background: ${theme.getValue('disabledIndicatorBorderColor')};
			opacity: 0.7;

			&:hover,
			&:active {
				--radio-border-color: ${theme.getValue('disabledIndicatorBorderColor')};
				--radio-outline: none;
			}

			&[data-variant='outlined'] {
				--background: ${theme.getValue('disabledOutlinedItemBackground')};
				border: none;

				&:hover,
				&:active {
					box-shadow: none;
				}
			}
		}
	`,
	radio: styled.span`
		display: flex;
		width: ${theme.getValue('indicatorOuterSize')};
		height: ${theme.getValue('indicatorOuterSize')};
		border: 2px solid var(--radio-border-color);
		border-radius: 50%;
		align-items: center;
		justify-content: center;
		box-shadow: var(--radio-outline);
		background: var(--radio-background);
		flex: 0 0 auto;
		transition:
			border 150ms ease-out,
			box-shadow 50ms ease-out;
	`,
	indicator: styled(RadioGroup.Indicator)`
		display: block;
		width: ${theme.getValue('indicatorInnerSize')};
		height: ${theme.getValue('indicatorInnerSize')};
		background: var(--indicator-background);
		border-radius: 50%;
		animation: ${overlayShow} 100ms ease-out;
	`,
	text: styled.span`
		display: inline-flex;
		flex-direction: column;

		// To avoid layout shift, this pseudo-element is added.
		&::after {
			content: attr(data-text);
			content: attr(data-text) / '';
			height: 0;
			visibility: hidden;
			overflow: hidden;
			user-select: none;
			pointer-events: none;
			font: ${theme.getValue('checkedItemFont')};
			@media speech {
				display: none;
			}
		}
	`,
};

export interface InputRadioItemProps extends RadioGroupItemProps {
	label: string;
}

export function CellulaInputRadioItem({ label, ...rest }: InputRadioItemProps, ref: ForwardedRef<HTMLButtonElement>): JSX.Element {
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const context = useInputRadioContext();

	return (
		<elements.item
			data-variant={context.variant}
			data-invalid={context.invalid ?? undefined}
			aria-readonly={context.readOnly ?? undefined}
			data-read-only={context.readOnly ?? undefined}
			data-disabled={context.disabled ?? undefined}
			{...rest}
			ref={ref}
		>
			<elements.radio>
				<elements.indicator />
			</elements.radio>
			<elements.text data-text={label}>{label}</elements.text>
		</elements.item>
	);
}
